import { computeTrend } from "../utils";
import { CONFIG } from "./CONFIG";

// Build the issue-filter findQuery: the period's date findQuery PLUS a constraint
// that the annotation carries one of the dissatisfaction-category signals. The
// signal _ids come from context.signals; the BE coerces them to ObjectId
// (useObjectId on annotations). Returns undefined when there's no base query or
// no issue signals (→ the widget disables the fetch).
export const buildIssueFindQuery = (baseFindQuery, signals) => {
  const issueSignalIds = (signals || [])
    .filter((s) => CONFIG.issueCategories.includes(s.category))
    .map((s) => s._id);
  if (!baseFindQuery || issueSignalIds.length === 0) return undefined;
  return { ...baseFindQuery, "annotations.tags.tags.data._id": { $in: issueSignalIds } };
};

// Assemble the per-issue spine. An "issue" mirrors v1's TeamLensBlock: a TOPIC
// that appears on dissatisfaction ("issue") annotations, grouped by its subTheme
// ("topic category"). Counts come from a single issue-filtered aggrMetrics over
// the topics dimension — so each topic's count is the number of DISTINCT issue
// annotations mentioning it (deduped per annotation, no signal-weighted
// over-count). The topic→subTheme mapping rides on context.topics.
//
// `currCounts` / `prevCounts` are { [tagId]: count } maps from the issue-filtered
// aggrMetrics (current / previous period). We drive off the FULL topic list and
// keep only topics that occurred on issue annotations this window.
export const assembleIssues = (topics, currCounts = {}, prevCounts = {}) => {
  const issues = (topics || [])
    .map((t) => {
      const currentCount = currCounts[t._id] ?? 0;
      const prevCount = prevCounts[t._id] ?? 0;
      return {
        name: t.display,
        topicId: t._id,
        subThemeId: t.subThemeId || null,
        subTheme: t.subThemeDisplay || null,
        currentCount,
        prevCount,
        ...computeTrend(currentCount, prevCount, CONFIG.trendThresholdPct),
      };
    })
    .filter((t) => t.currentCount > 0)
    .sort((a, b) => b.currentCount - a.currentCount);

  // Distinct topic subThemes ("topic categories") for the dropdown.
  const seen = new Map();
  for (const t of issues) {
    if (t.subThemeId && !seen.has(t.subThemeId)) seen.set(t.subThemeId, t.subTheme || t.subThemeId);
  }
  const subThemes = [...seen.entries()].map(([value, display]) => ({ value, display }));

  return { issues, subThemes };
};
